import {FC, useId} from 'react';
import {CountryDropdown} from '../components/CountryDropdown/CountryDropdown';
import {FormField} from 'ui';
import {GenderToggle} from '../components/GenderToggle/GenderToggle';
import {StepLayout} from 'features/registration';
import {TextInput} from '../components/TextInput';
import {
	extractInnDigits,
	formatInnDisplay,
	InnStepValues,
	isKyrgyzstanRegistrationOksm,
	PersonalInfoStepValues,
	requiresRussianNameLabels,
} from '../../model';
import styles from './PersonalInfoStep.module.css';

type InnFieldProps = {
	values: InnStepValues;
	error?: string | null;
	onChange: (patch: Partial<InnStepValues>) => void;
};

type Props = {
	values: PersonalInfoStepValues;
	onChange: (patch: Partial<PersonalInfoStepValues>) => void;
	/** Скрыть при правке со сверки: смена гражданства идёт через подтверждение и возврат на этот экран. */
	hideCitizenship?: boolean;
	/** Заголовок экрана (например, при редактировании со сверки). */
	title?: string;
	/** Поле ИНН первым в списке; без пропа поле не показывается. */
	inn?: InnFieldProps;
};

export const PersonalInfoStep: FC<Props> = ({
	values,
	onChange,
	hideCitizenship = false,
	title = 'Давайте познакомимся',
	inn,
}) => {
	const genderLabelId = useId();
	const useRussianNameLabels = requiresRussianNameLabels(
		values.citizenshipOksmCode,
		values.residenceOksmCode,
	);
	const isKyrgyzstan = isKyrgyzstanRegistrationOksm(values.citizenshipOksmCode);
	const secondNameLabel = useRussianNameLabels ? 'Фамилия (на русском)' : 'Фамилия';
	const firstNameLabel = useRussianNameLabels ? 'Имя (на русском)' : 'Имя';

	return (
		<StepLayout variant="compact">
			<StepLayout.Title>{title}</StepLayout.Title>

			<StepLayout.Content className={styles.fields}>
				{inn ? (
					<FormField label="ИНН" error={inn.error}>
						<TextInput
							name="inn"
							value={formatInnDisplay(inn.values.inn)}
							placeholder="0000 0000 0000"
							inputMode="numeric"
							autoComplete="off"
							aria-invalid={Boolean(inn.error)}
							onChange={(event) => inn.onChange({inn: extractInnDigits(event.target.value)})}
							onClear={() => inn.onChange({inn: ''})}
						/>
					</FormField>
				) : null}

				{hideCitizenship ? null : (
					<FormField label="Гражданство">
						<CountryDropdown
							value={values.citizenshipOksmCode}
							countryName={values.citizenshipCountryName}
							drawerTitle="Выберите страну"
							onChange={(nextCitizenshipOksmCode, country) =>
								onChange({
									citizenshipOksmCode: nextCitizenshipOksmCode,
									citizenshipCountryName: country.name,
								})
							}
						/>
					</FormField>
				)}

				<FormField label="Страна проживания (налогового резидентства)">
					<CountryDropdown
						value={values.residenceOksmCode}
						countryName={values.residenceCountryName}
						drawerTitle="Выберите страну"
						onChange={(nextResidenceOksmCode, country) =>
							onChange({
								residenceOksmCode: nextResidenceOksmCode,
								residenceCountryName: country.name,
							})
						}
					/>
				</FormField>

				<FormField label="Пол" as="div" labelId={genderLabelId}>
					<GenderToggle
						labelledBy={genderLabelId}
						value={values.gender}
						onChange={(gender) => onChange({gender})}
					/>
				</FormField>

				<FormField label={secondNameLabel}>
					<TextInput
						name="secondName"
						value={values.secondName}
						placeholder={isKyrgyzstan ? 'Аалы' : 'Смирнов'}
						autoComplete="family-name"
						onChange={(event) => onChange({secondName: event.target.value})}
						onClear={() => onChange({secondName: ''})}
					/>
				</FormField>

				<FormField label={firstNameLabel}>
					<TextInput
						name="firstName"
						value={values.firstName}
						placeholder={isKyrgyzstan ? 'Адилай' : 'Иван'}
						autoComplete="given-name"
						onChange={(event) => onChange({firstName: event.target.value})}
						onClear={() => onChange({firstName: ''})}
					/>
				</FormField>

				<FormField label="Отчество (если есть)">
					<TextInput
						name="patronymic"
						value={values.patronymic}
						placeholder={isKyrgyzstan ? 'Жаныбаевич' : 'Иванович'}
						autoComplete="additional-name"
						onChange={(event) => onChange({patronymic: event.target.value})}
						onClear={() => onChange({patronymic: ''})}
					/>
				</FormField>

				<FormField label="E-mail">
					<TextInput
						name="email"
						type="email"
						value={values.email}
						placeholder="name@example.com"
						autoComplete="email"
						inputMode="email"
						onChange={(event) => onChange({email: event.target.value})}
						onClear={() => onChange({email: ''})}
					/>
				</FormField>
			</StepLayout.Content>
		</StepLayout>
	);
};
